import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Globe, 
  Palette, 
  Terminal, 
  Lightbulb,
  Megaphone,
  Search,
  BarChart3,
  Shield,
  Bot,
  Calendar,
  Database,
  Store,
  WholeWord as WordPress,
  Settings,
  CreditCard,
  Zap,
  Server,
  Mail,
  MessageSquare,
  Plus,
  Pencil,
  Trash2,
  ArrowRight,
  X,
  Menu,
  ChevronDown,
  LifeBuoy,
  CheckCircle2
} from 'lucide-react';
import FormSuccess from '../components/FormSuccess';

const services = [
  { 
    id: 'web-development',
    icon: Globe, 
    title: 'Vefsíðugerð', 
    description: 'Sérsniðnar veflausnir'
  },
  { 
    id: 'web-design',
    icon: Palette, 
    title: 'Vefhönnun', 
    description: 'Nútímaleg hönnun'
  },
  { 
    id: 'development',
    icon: Terminal, 
    title: 'Vefþróun', 
    description: 'Öflug forritun'
  },
  { 
    id: 'consulting',
    icon: Lightbulb, 
    title: 'Ráðgjöf', 
    description: 'Fagleg ráðgjöf'
  },
  { 
    id: 'marketing',
    icon: Megaphone, 
    title: 'Markaðssetning', 
    description: 'Stafræn markaðssetning'
  },
  { 
    id: 'seo',
    icon: Search, 
    title: 'Leitarvélabestun', 
    description: 'SEO þjónusta'
  },
  { 
    id: 'analytics',
    icon: BarChart3, 
    title: 'Greiningar', 
    description: 'Skýrslur og greiningar'
  },
  { 
    id: 'maintenance',
    icon: Shield, 
    title: 'Viðhald og öryggi', 
    description: 'Örugg þjónusta'
  }
];

const solutions = [
  { 
    id: 'ai',
    icon: Bot, 
    title: 'Gervigreind', 
    description: 'AI lausnir fyrir fyrirtæki'
  },
  { 
    id: 'booking',
    icon: Calendar, 
    title: 'Bókunarkerfi', 
    description: 'Sjálfvirk bókunarkerfi'
  },
  { 
    id: 'subscription',
    icon: Database, 
    title: 'Áskriftarkerfi', 
    description: 'Recurring revenue lausnir'
  },
  { 
    id: 'ecommerce',
    icon: Store, 
    title: 'Netverslun', 
    description: 'WooCommerce / Shopify'
  },
  { 
    id: 'pos',
    icon: CreditCard, 
    title: 'Kassakerfi', 
    description: 'POS lausnir'
  },
  { 
    id: 'wordpress',
    icon: WordPress, 
    title: 'WordPress', 
    description: 'WordPress lausnir'
  },
  { 
    id: 'wordpress-express',
    icon: Zap, 
    title: 'WordPress Express', 
    description: 'Hraðvirk WordPress lausn'
  },
  { 
    id: 'hosting',
    icon: Server, 
    title: 'Vefhýsing', 
    description: 'Örugg hýsing'
  },
  { 
    id: 'email',
    icon: Mail, 
    title: 'Tölvupóstur', 
    description: 'Tölvupóstþjónusta'
  },
  { 
    id: 'maintenance',
    icon: Shield, 
    title: 'Vefumsjón', 
    description: 'Viðhald og öryggi'
  },
  {
    id: 'chat',
    icon: MessageSquare,
    title: 'Netspjall',
    description: 'AI spjallmenni'
  }
];

const GetQuote = () => {
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedSolutions, setSelectedSolutions] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    budget: '',
    timeline: ''
  });

  const handleServiceToggle = (serviceId: string) => {
    setSelectedServices(prev => 
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSolutionToggle = (solutionId: string) => {
    setSelectedSolutions(prev => 
      prev.includes(solutionId)
        ? prev.filter(id => id !== solutionId)
        : [...prev, solutionId]
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          'form-name': form.getAttribute('name') || '',
          ...formData,
          selected_services: selectedServices.join(', '),
          selected_solutions: selectedSolutions.join(', ')
        }).toString()
      });
      
      if (response.ok) {
        setShowSuccess(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          budget: '',
          timeline: ''
        });
        setSelectedServices([]);
        setSelectedSolutions([]);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Villa kom upp við að senda form. Vinsamlegast reyndu aftur.');
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Fáðu tilboð í þitt verkefni
            </h1>
            <p className="text-xl text-gray-600">
              Segðu okkur frá þínum hugmyndum og við finnum bestu lausnina fyrir þig
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-12">
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white rounded-2xl shadow-sm p-8">
                <form 
                  name="quote"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  onSubmit={handleSubmit}
                  className="space-y-8"
                >
                  <input type="hidden" name="form-name" value="quote" />
                  <p hidden>
                    <label>
                      Don't fill this out: <input name="bot-field" />
                    </label>
                  </p>
                  <input 
                    type="hidden" 
                    name="selected_services" 
                    value={selectedServices.join(', ')} 
                  />
                  <input 
                    type="hidden" 
                    name="selected_solutions" 
                    value={selectedSolutions.join(', ')} 
                  />

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Veldu þjónustu</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {services.map((service) => (
                        <motion.div
                          key={service.id}
                          className={`p-4 border rounded-lg cursor-pointer transition-all ${
                            selectedServices.includes(service.id)
                              ? 'border-[#EA295A] bg-[#EA295A]/5'
                              : 'border-gray-200 hover:border-[#EA295A]/50'
                          }`}
                          onClick={() => handleServiceToggle(service.id)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <service.icon className={`w-6 h-6 ${
                                selectedServices.includes(service.id)
                                  ? 'text-[#EA295A]'
                                  : 'text-gray-400'
                              }`} />
                            </div>
                            <div className="ml-3">
                              <h4 className="text-sm font-medium text-gray-900">{service.title}</h4>
                              <p className="mt-1 text-xs text-gray-500">{service.description}</p>
                            </div>
                            {selectedServices.includes(service.id) && (
                              <CheckCircle2 className="ml-auto w-5 h-5 text-[#EA295A]" />
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Veldu lausnir</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {solutions.map((solution) => (
                        <motion.div
                          key={solution.id}
                          className={`p-4 border rounded-lg cursor-pointer transition-all ${
                            selectedSolutions.includes(solution.id)
                              ? 'border-[#EA295A] bg-[#EA295A]/5'
                              : 'border-gray-200 hover:border-[#EA295A]/50'
                          }`}
                          onClick={() => handleSolutionToggle(solution.id)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="flex items-start">
                            <div className="flex-shrink-0">
                              <solution.icon className={`w-6 h-6 ${
                                selectedSolutions.includes(solution.id)
                                  ? 'text-[#EA295A]'
                                  : 'text-gray-400'
                              }`} />
                            </div>
                            <div className="ml-3">
                              <h4 className="text-sm font-medium text-gray-900">{solution.title}</h4>
                              <p className="mt-1 text-xs text-gray-500">{solution.description}</p>
                            </div>
                            {selectedSolutions.includes(solution.id) && (
                              <CheckCircle2 className="ml-auto w-5 h-5 text-[#EA295A]" />
                            )}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Upplýsingar</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                          Nafn
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Netfang
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                          Sími
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                          Fyrirtæki
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Verkefnalýsing</h3>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                        Lýsing á verkefni
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
                          Áætlaður kostnaður
                        </label>
                        <select
                          id="budget"
                          name="budget"
                          value={formData.budget}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        >
                          <option value="">Veldu kostnað</option>
                          <option value="100-500">100.000 - 500.000 kr.</option>
                          <option value="500-1000">500.000 - 1.000.000 kr.</option>
                          <option value="1000-2000">1.000.000 - 2.000.000 kr.</option>
                          <option value="2000-3000">2.000.000 - 3.000.000 kr.</option>
                          <option value="3000-5000">3.000.000 - 5.000.000 kr.</option>
                          <option value="5000-10000">5.000.000 - 10.000.000 kr.</option>
                          <option value="10000+">10.000.000 kr. +</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-1">
                          Tímarammi
                        </label>
                        <select
                          id="timeline"
                          name="timeline"
                          value={formData.timeline}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EA295A] focus:border-transparent"
                        >
                          <option value="">Veldu tímaramma</option>
                          <option value="1v-2v">1-2 vikur</option>
                          <option value="2v-4v">2-4 vikur</option>
                          <option value="1-2">1-2 mánuðir</option>
                          <option value="2-3">2-3 mánuðir</option>
                          <option value="3-6">3-6 mánuðir</option>
                          <option value="6+">6+ mánuðir</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <motion.button
                    type="submit"
                    className="w-full bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center justify-center"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    SENDA FYRIRSPURN
                    <ArrowRight className="ml-2" />
                  </motion.button>
                </form>
              </div>
            </motion.div>

            <motion.div
              className="lg:col-span-1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="bg-white rounded-2xl shadow-sm p-8 sticky top-32">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Valdar lausnir</h3>
                
                {selectedServices.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Þjónusta</h4>
                    <div className="space-y-2">
                      {selectedServices.map((serviceId) => {
                        const service = services.find(s => s.id === serviceId);
                        if (!service) return null;
                        return (
                          <div key={serviceId} className="flex items-center text-gray-600">
                            <service.icon className="w-5 h-5 text-[#EA295A] mr-2" />
                            <span>{service.title}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {selectedSolutions.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Lausnir</h4>
                    <div className="space-y-2">
                      {selectedSolutions.map((solutionId) => {
                        const solution = solutions.find(s => s.id === solutionId);
                        if (!solution) return null;
                        return (
                          <div key={solutionId} className="flex items-center text-gray-600">
                            <solution.icon className="w-5 h-5 text-[#EA295A] mr-2" />
                            <span>{solution.title}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {selectedServices.length === 0 && selectedSolutions.length === 0 && (
                  <p className="text-gray-500 text-center">
                    Veldu þjónustu eða lausnir til að sjá yfirlit
                  </p>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {showSuccess && (
        <FormSuccess 
          message="Takk fyrir fyrirspurnina! Við munum senda þér tilboð eins fljótt og auðið er."
          onClose={() => setShowSuccess(false)}
        />
      )}
    </>
  );
};

export default GetQuote;